import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import ThankviewRecorder from "../thankview-recorder";
import { useRecorder } from "./recorder-context";
import ThankviewDefaultSettings from "../thankview-default-settings";

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    openModal: null,
    currentComponent: null,
    modalProps: {},
    defaultOpened: false,
  });

  const { state, stopWebcam } = useRecorder();

  // Set defaultOpened to false on mount and check sessionStorage
  useEffect(() => {
    const storedDefaultOpened = sessionStorage.getItem("defaultOpened");
    if (storedDefaultOpened) {
      setModalState((prevState) => ({
        ...prevState,
        defaultOpened: JSON.parse(storedDefaultOpened),
      }));
    } else {
      setModalState((prevState) => ({
        ...prevState,
        defaultOpened: false,
      }));
    }
  }, []);

  const openModal = (modalName, component, props = {}) => {
    if (modalName === "Set Video Defaults") {
      if (!modalState.defaultOpened) {
        setModalState((prevState) => {
          const newState = {
            openModal: "Set Video Defaults",
            currentComponent: component,
            modalProps: props,
            defaultOpened: true,
          };
          sessionStorage.setItem("defaultOpened", JSON.stringify(true)); // Update sessionStorage
          return newState;
        });
      } else {
        // If defaultOpened is true, open the recorder modal instead
        setModalState({
          openModal: "Record Your Video",
          currentComponent: ThankviewRecorder,
          modalProps: { size: "1060px", withActions: false, height: "735px" },
        });
      }
    } else {
      setModalState({
        openModal: modalName,
        currentComponent: component,
        modalProps: props,
      });
    }
  };

  const openDefaultSettings = (modalName, component, props = {}) => {
    setModalState({
      openModal: "Set Video Defaults",
      currentComponent: ThankviewDefaultSettings,
      modalProps: props,
    });
  };

  const closeModal = () => {
    if (state.webcamStream) {
      stopWebcam();
    }
    setModalState((prevState) => {
      sessionStorage.setItem("defaultOpened", JSON.stringify(false)); // Reset sessionStorage
      return {
        openModal: null,
        currentComponent: null,
        modalProps: {},
        defaultOpened: false, // Ensure defaultOpened is false on close
      };
    });
  };

  return (
    <ModalContext.Provider value={{ modalState, openModal, closeModal, openDefaultSettings }}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => useContext(ModalContext);

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
